import { SimpleRope, Point, Texture } from 'pixi.js';
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import LineQueue2 from "./LineQueue2";
gsap.registerPlugin(MotionPathPlugin);

const TailLength = 220;
const TailWidth = 2;
const TailBasicSpeed = 140; //255;

class LineAnimator2 {
    constructor(path, front, direction = true, delay = 0) {
        this.front = front;
        this.delay = delay * 0.2;

        this.direction = direction; // Math.random() > 0.5;

        this.path = path;
        this.speed = TailBasicSpeed;
        this.setLength();
        this.dotReachedEnd = false;
        this.isFinished = false;
        this.isClosed = this.isPathClosed();
        this.finalPoints = [];

        this.pathQueue = new LineQueue2(this.length);
        this.fps = 120;

        this.generateLine()
        this.start();
    }

    isPathClosed() {
        return this.path.trim().toUpperCase().endsWith('Z');
    }

    generateLine(reInit = false) {
        const rawPath = MotionPathPlugin.getRawPath(this.path);
        const firstPoint = MotionPathPlugin.getPositionOnPath(rawPath, 0);
        this.dot = new Point(firstPoint.x, firstPoint.y);
        this.prevEase = 0;
        this.isFinalizing = false;

        const ropeTexture = this.createGradientTexture(this.length);

        this.points = [];
        for (let i = 0; i < this.length; i++) {
            this.points.push(new Point(this.dot.x, this.dot.y));
        }

        if (reInit) {
            this.front.removeChild(this.curve);
            this.curve.texture.destroy(true);
            this.curve.destroy();
        }

        this.curve = new SimpleRope(ropeTexture, this.points);

        this.front.addChild(this.curve);
    }

    createGradientTexture(length) {
        const canvas = document.createElement('canvas');
        canvas.width = length;
        canvas.height = TailWidth;

        const ctx = canvas.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
        const firstStop = 7 / canvas.width;
        const secondStop = 1 - firstStop;

        gradient.addColorStop(0, 'rgba(62,126,161,0)');
        gradient.addColorStop(firstStop, 'rgba(62,126,161, .5)');
        gradient.addColorStop(secondStop, 'rgba(62,126,161, .5)');
        gradient.addColorStop(1, 'rgba(62,126,161,0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.restore();
        ctx.closePath();
        canvas.remove();

        return Texture.from(canvas);
    }

    setLength() {
        this.pathLength = MotionPathPlugin.getLength(this.path);
        const tailLength = Math.min(TailLength, Math.floor(this.pathLength * 0.3));
        this.length = Math.floor(Math.random() * (tailLength - 30 + 1) + 30);
    }

    reInit(path, direction = true) {
        this.path = path;
        this.direction = direction;
        this.setLength();
        this.dotReachedEnd = false;
        this.isFinished = false;
        this.isFinalizing = false;
        this.isClosed = this.isPathClosed();
        this.pathQueue.clear();
        this.pathQueue.resize(this.length);

        this.generateLine(true);

        this.start();
    }

    generateFinalPoints() {
        const rawPath = MotionPathPlugin.getRawPath(this.path);
        const duration = this.pathLength / this.speed;
        const steps = Math.round(duration * this.fps);
        const points = [];
        for (let i = 0; i < steps; i++) {
            const point = MotionPathPlugin.getPositionOnPath(rawPath, i / (steps - 1));
            points.push(point);
        }
        this.finalPoints = points;
    }

    start() {
        this.generateFinalPoints();
        this.currentStep = 0;
        this.updatePathQueue();
    }

    updatePathQueue() {
        if (this.currentStep >= this.finalPoints.length) {
            this.dotReachedEnd = true;
        } else {
            this.dot = this.finalPoints[this.currentStep];
            this.pathQueue.enqueue(this.dot);

            this.currentStep++;
        }
    }

    draw() {
        this.updatePathQueue();
        if(!this.isFinished) {
            if(this.isFinalizing){
                if(this.finalPoints.length >= 4) {
                    let headLength = Math.max(Math.round(this.finalPoints.length / 7), 2);
                    if (headLength < 1) headLength = 1;
                    let tailLength = headLength;
                    let bodyLength = this.finalPoints.length - headLength - tailLength;

                    const headPoints = this.finalPoints.slice(0, headLength);
                    const bodyPoints = this.finalPoints.slice(headLength, headLength + bodyLength);
                    const tailPoints = this.finalPoints.slice(headLength + bodyLength, this.finalPoints.length);
                    let j = 0;
                    if(headLength > 0) {
                        headPoints.forEach((point, i) => {
                            this.points[j].x = point.x;
                            this.points[j].y = point.y;
                            j += 1;
                        });
                    }
                    if (bodyLength > 0) {
                        bodyPoints.forEach((point, i) => {
                            this.points[j].x = point.x;
                            this.points[j].y = point.y;
                            j += 1;
                        });
                    }
                    const restPointsLength = this.length - headLength - bodyLength - tailLength;
                    if(restPointsLength > 0) {
                        const firstTailPoint = tailPoints[0];
                        for(let i = 0; i < restPointsLength; i++){
                            this.points[j].x = firstTailPoint.x;
                            this.points[j].y = firstTailPoint.y;
                            j += 1;
                        }
                    }
                    if(tailLength > 0) {
                        tailPoints.forEach((point, i) => {
                            this.points[j].x = point.x;
                            this.points[j].y = point.y;
                            j += 1;
                        });
                    }
                    this.finalPoints.shift();
                } else {
                    this.isFinished = true;
                }
            } else {
                let j = 0;

                const pointsDiff = this.points.length - this.pathQueue.size();

                if (pointsDiff > 0 && !this.dotReachedEnd) {
                    // fill the gap with the first point
                    for (let i = 0; i < pointsDiff; i++) {
                        const peek = this.pathQueue.peek();
                        if (peek) {
                            this.points[i].x = peek.x;
                            this.points[i].y = peek.y;
                        }
                    }

                    j = pointsDiff;
                }

                for (let point of this.pathQueue) {
                    this.points[j].x = point.x;
                    this.points[j].y = point.y;
                    j++;
                }

                if (pointsDiff > 0 && this.dotReachedEnd) {
                    let easeDiff = 0;
                    let deltaDiff = 0;
                    let easeVal = 0;
                    if (this.isClosed) {
                        const diffRatio = Math.round(pointsDiff / this.length * 100) / 100;
                        easeVal = Math.round(this.easeOutQuad(1 - diffRatio) * 100) / 100;

                        if (diffRatio < 0.575) {
                            easeDiff = Math.round(pointsDiff * easeVal * .5);
                            this.lastEaseDiff = easeDiff;
                        } else {
                            easeDiff = Math.round(this.lastEaseDiff * 1.007);
                            this.lastEaseDiff = easeDiff;
                        }

                        // easeDiff = Math.round(pointsDiff * easeVal);
                        deltaDiff = pointsDiff - easeDiff;
                    }
                    if (deltaDiff > 0) {
                        for (let i = 0; i < deltaDiff; i++) {
                            const lastPoint = this.pathQueue.last();
                            if (lastPoint) {
                                this.points[j].x = lastPoint.x;
                                this.points[j].y = lastPoint.y;
                            }
                            j++;
                        }
                    }
                    if (easeDiff > 0) {
                        for (let i = 0; i < easeDiff; i++) {
                            const nextPoint = this.pathQueue.backupQueue[i];
                            if (nextPoint) {
                                this.points[j].x = nextPoint.x;
                                this.points[j].y = nextPoint.y;
                            }
                            j++;
                        }
                        this.easeDiff = easeDiff;
                    }
                }

                if (this.dotReachedEnd) {
                    if (this.pathQueue.isEmpty()) {
                        if (this.isClosed && this.easeDiff > 0) {
                            this.finalPoints = this.pathQueue.backupQueue.slice(0, this.easeDiff);
                            this.isFinalizing = true;
                        } else {
                            this.isFinished = true;
                        }
                    } else {
                        this.pathQueue.dequeue();
                    }
                }
            }
        }
    }

    easeOutQuad(t) {
        return 1 - (1 - t) * (1 - t);
    }

    easeInOutQuad(t) {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }


    destroy() {
        this.front.removeChild(this.curve);
        this.curve.destroy();
        this.curve = null;
        this.dot = null;
        this.pathQueue = null;
        this.path = null;
        this.front = null;
        this.back = null;
        this.points = null;
        this.finalPoints = null;
        this.isFinished = null;
        this.isClosed = null;
        this.dotReachedEnd = null;
        this.isFinalizing = null;
        this.easeDiff = null;
        this.lastEaseDiff = null;
        this.currentStep = null;
        this.length = null;
    }
}

export default LineAnimator2;
